import { Component, OnInit, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { RowSelectedEvent } from 'ag-grid-community';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { User } from '@models/user';
import { UserService } from '@services/user.service';
import { formateDateTime, getCurrencySymbol } from '@utils/utils';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { PrefixLabels, SubaccountService } from '@services/subaccount.service';
import { ApiGetSubaccounts, SearchTypeSubFilter } from '@models/subaccount/subaccount';
import { ResellerService } from '@services/reseller.service';
import { Router } from '@angular/router';
import { GridSlideToggleComponent } from '@ui/components/grid-slide-toggle/grid-slide-toggle.component';
import { LoginService } from 'src/app/core/login/login.service';
import { CreditEatMode } from '@models/subaccount/credit-movements';
import { TranslateService } from '@ngx-translate/core';
import { LoaderService } from '@services/loader.service';
import { finalize } from 'rxjs/operators';
import { GridReadyEvent } from 'ag-grid-community';
import { SmsAvailability } from '@models/superaccount/settings';
import { SuperaccountService } from '@services/superaccount.service';
import { nations } from '@utils/constants';
import * as _ from 'lodash';

@Component({
  selector: 'wsb-subaccount-manage',
  templateUrl: './subaccount-manage.component.html',
  styleUrls: ['./subaccount-manage.component.scss'],
  animations: [
    trigger('gridIsLoaded', [
      state('hide', style({ opacity: 0 })),
      state('show', style({ opacity: 1 })),
      transition('hide => show', animate('150ms', style({ opacity: 1 }))),
      transition('show => hide', animate('150ms', style({ opacity: 0 })))
    ])
  ],
  standalone: false
})
export class SubaccountManageComponent implements OnInit {
  @ViewChild('pageDashboard', { static: true }) $pageDashboard;
  @ViewChild('modalConfirmImpersonate') modalConfirmImpersonate;
  @ViewChild('grid') grid: AtlasGridComponent;
  @ViewChild('modalTransferCreditsType0') modalTransferCreditsType0;
  @ViewChild('modalTransferCreditsType2') modalTransferCreditsType2;
  modalTransferCreditsModal: NgbModalRef;
  modalConfirmImpersonateModal: NgbModalRef;

  user: User | null;
  credits;
  tableColumnDefs: any = [];
  tableGridOptions = null;
  subaccountModal;
  subToimpersonate;
  transferType;
  nations = [];
  components = {
    buttonGroupRenderer: ButtonGroupRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent,
    slideRenderer: GridSlideToggleComponent
  };
  tableLoading: boolean;
  paginationPageSize = 10;
  frameworkGroupComponents = {
    buttonGroupRenderer: ButtonGroupRendererComponent
  };
  gridAnimationState = 'hide';

  constructor(
    private userService: UserService,
    private subaccountService: SubaccountService,
    private resellerService: ResellerService,
    private loginService: LoginService,
    private modalService: NgbModal,
    private router: Router,
    private loaderService: LoaderService,
    private translateService: TranslateService,
    private superaccountService: SuperaccountService
  ) {
    nations.forEach(x => {
      this.nations.push({
        id: x[0],
        label: x[1]
      });
    });

    this.tableColumnDefs = [
      {
        headerNameTranslate: 'account_userid',
        field: 'idUser',
        colPrefKey: 'idUser'
      },
      {
        headerNameTranslate: 'settings_subaccounts_table_field_details',
        field: 'login',
        colPrefKey: 'login',
        suppressSizeToFit: true,
        cellRenderer: sub => {
          if (sub && sub.data) {
            let details = '<b>' + sub.data?.login + '</b><br>' + sub.data?.name + ' ' + sub.data?.surname + '<br>' + sub.data?.company;
            if (sub.data?.nomadicNumber) {
              details += '<br>' + sub.data?.nomadicNumber;
            }
            return details;
          }
          return '';
        },
        cellStyle: { 'line-height': '20px' }
      },
      {
        headerNameTranslate: 'common_credits',
        field: 'credits',
        colPrefKey: 'credits',
        cellRenderer: ppm => {
          return this.getLabelsCredits(ppm.data);
        },
        cellStyle: { 'line-height': '20px', 'overflow-y': 'auto' },
        cellClass: 'center-child'
      },
      {
        headerNameTranslate: 'common_data_creation',
        field: 'createTime',
        colPrefKey: 'creationDate',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => (params && params.data ? formateDateTime(params.data.createTime, false) : '')
        }
      },
      {
        headerNameTranslate: 'settings_subaccounts_table_field_last_recharge',
        field: 'lastRechargeDate',
        colPrefKey: 'lastRecharge',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => (params && params.data?.lastRechargeDate ? formateDateTime(params.data.lastRechargeDate, false) : '')
        }
      },
      {
        headerNameTranslate: 'settings_subaccounts_table_field_lock',
        field: 'active',
        cellRenderer: 'slideRenderer',
        cellRendererParams: {
          labelOn: 'common_unlock',
          labelOff: 'common_lock',
          checked: params => (params?.data ? params.data.active : false),
          // Conditions for Lock Unlock
          // toggleEvent.valueToggle = true => !valueToggle = false means Active (isLock=false)
          // toggleEvent.valueToggle = false => !valueToggle = true means Blocked (isLock=true)
          toggleEvent: (params: any, valueToggle: any) =>
            this.subaccountService.lockUnlockSubaccount(params.data.idUser, !valueToggle).subscribe(() => {
              params.api.refreshCells();
            })
        }
      },
      {
        headerNameTranslate: 'common_actions',
        suppressCellFocus: true,
        cellRenderer: 'buttonGroupRenderer',
        cellRendererParams: {
          items: [
            {
              onClick: subaccount => this.router.navigate(['/reseller/subaccounts-settings'], { queryParams: { id: subaccount.idUser } }),
              label: 'common_edit'
            },
            {
              onClick: subaccount => this.openModalConfirmImpersonate(subaccount),
              label: 'settings_subaccounts_table_field_impersonate',
              hide: subaccount => (subaccount ? subaccount.disableSubaccountImpersonation : false)
              // variant: 'danger',
            },
            {
              onClick: subaccount => {
                this.transferType = subaccount.creditEatMode;
                this.openTransfertCreditsModal(subaccount);
              },
              hide: subaccount => this.isHiddenForSuperaccountCreditMode(subaccount?.creditEatMode),
              label: 'settings_subaccounts_movecredit'
            },
            {
              onClick: subaccount => this.goSubaccountHistoryPageById(subaccount),
              hide: subaccount => this.isHiddenForSuperaccountCreditMode(subaccount?.creditEatMode),
              label: 'settings_subaccounts_table_field_history'
              // variant: 'primary',
            }
          ]
        },
        cellClass: ['button-group', 'text-end'],
        maxWidth: 82,
        field: 'actions'
      }
    ];
  }

  ngOnInit() {
    this.getUser();
    const that = this;
    this.loaderService.show();

    this.resellerService.getProfiles().subscribe(data => {
      let profiles: Array<{ id: string; value: string }> = [];
      for (const profile of data.data) {
        profiles = [
          ...profiles,
          {
            id: profile.idSubaccountProfile.toString(),
            value: profile.name
          }
        ];
      }

      this.tableGridOptions = AtlasGridOptions.deserialize({
        onGridReady: (event: GridReadyEvent) => {
          this.gridAnimationState = 'show';
        },
        onRowSelected: (event: RowSelectedEvent) => {
          if (event.data.shared && event.node.isSelected()) {
            event.node.setSelected(false);
          } else {
            this.grid.selectionChange.emit(event);
          }
        },
        suppressRowClickSelection: true,
        pagination: true,
        paginationPageSize: this.paginationPageSize,
        loadFunction: this.reloadList.bind(that),
        filters: this.getFilters(profiles),
        rowHeight: 115, // 70,
        sortable: true
      });
    });
  }

  /*
   * btn TrasferCredit and btn History are hidded for only CreditEatMode.SUPERACCOUNT_CREDITS
   *
   */
  private isHiddenForSuperaccountCreditMode(creditEatMode) {
    return creditEatMode === CreditEatMode.SUPERACCOUNT_CREDITS ? true : false;
  }

  getApiGetSubaccountOptions() {
    let apiGetSubaccounts = new ApiGetSubaccounts();
    const basicFilters = {};
    ['login', 'credits', 'name', 'surname', 'company', 'creditsFrom', 'creditsTo', 'idProfile', 'type'].map(x => {
      basicFilters[x] = this.grid.filtersModel[x]?.value ? this.grid.filtersModel[x]?.value : this.grid.filtersModel[x];
    });

    apiGetSubaccounts = basicFilters;
    // console.log(apiGetSubaccounts);

    return apiGetSubaccounts;
  }

  reloadList(startRow?, endRow?, sort?) {
    return this.subaccountService
      .getSubaccounts(
        1 + startRow / (endRow - startRow),
        endRow - startRow,
        sort && sort.length ? this.getColId(sort[0].colId) : null,
        sort && sort.length ? (sort[0].sort === 'desc' ? 'd' : 'a') : null,
        this.getApiGetSubaccountOptions()
      )
      .pipe(finalize(() => this.loaderService.hide()));
  }

  getColId(id: string) {
    if (id === 'idUser') {
      return 'ID_USER';
    }
    if (id === 'login') {
      return 'LOGIN';
    }
    if (id === 'credits') {
      return 'CREDITS';
    }
    if (id === 'lastRechargeDate') {
      return 'LAST_RECHARGE';
    }
  }

  get showHelpAndShop(): boolean {
    return !this.userService.isSubaccount();
  }

  get getUserName(): string {
    if (this.user) {
      return this.user.profile.name;
    }
    return null;
  }

  getUser() {
    this.userService.getAuthenticationState().subscribe(loggedUser => {
      if (loggedUser) {
        this.user = loggedUser;
      }
    });
  }

  openModalConfirmImpersonate(data: any): void {
    this.subToimpersonate = data;
    this.modalConfirmImpersonateModal = this.modalService.open(this.modalConfirmImpersonate);
    this.modalConfirmImpersonateModal.result.then(
      result => {},
      reason => {}
    );
  }

  modalImpersonate(): void {
    this.impersonateSubaccount(this.subToimpersonate.idUser);
    this.modalConfirmImpersonateModal.close();
  }

  // openModalDelete(data: any): void {
  //   this.contactToDelete = data;
  //   this.modalDeleteModal = this.modalService.open(this.modalDelete);
  //   this.modalDeleteModal.result.then(
  //     result => {},
  //     reason => {}
  //   );
  // }

  impersonateSubaccount(idUser) {
    this.loginService.impersonateSubuser(idUser);
  }

  openTransfertCreditsModal(subaccount) {
    this.subaccountModal = subaccount;
    const options: NgbModalOptions = {
      size: 'lg'
    };

    if (this.transferType === 2) {
      options.windowClass = 'custom-transfer-credits-modal-width';
    }
    this.modalTransferCreditsModal = this.modalService.open(this.modalTransferCreditsType0, options);
    this.modalTransferCreditsModal.result.then(
      result => {},
      reason => {}
    );
  }

  closeModal(): void {
    this.grid.showLoading();
    this.grid.refresh();
    this.modalTransferCreditsModal.close();
  }

  // call history page
  goSubaccountHistoryPageById(data) {
    const id = data.idUser;
    this.router.navigate(['reseller/subaccounts-history'], { queryParams: { id } });
  }

  getFilters(profiles: any) {
    return [
      {
        name: 'login',
        label: 'account_login',
        type: 'text'
      },
      {
        name: 'name',
        label: 'contact_name',
        type: 'text'
      },
      {
        name: 'surname',
        label: 'contact_surname',
        type: 'text'
      },
      {
        name: 'company',
        label: 'account_company',
        type: 'text'
      },
      {
        name: 'creditsFrom',
        label: 'settings_subaccounts_search_creditsfrom',
        type: 'text'
      },
      {
        name: 'creditsTo',
        label: 'settings_subaccounts_search_creditsto',
        type: 'text'
      },
      {
        label: 'settings_subaccounts_search_profile_all',
        name: 'idProfile',
        type: 'dropdown',
        options: [
          ...profiles.map(x => {
            return {
              label: x.value,
              value: x.id
            };
          })
        ]
      },
      {
        label: 'settings_subaccounts_search_type_all',
        name: 'type',
        type: 'dropdown',
        options: [
          {
            label: 'common_select_all',
            value: SearchTypeSubFilter.ALL,
            translate: true
          },
          {
            label: 'settings_subaccounts_search_type_final',
            value: SearchTypeSubFilter.ONLY_FINAL_SUB,
            translate: true
          },
          {
            label: 'settings_subaccounts_search_type_reseller',
            value: SearchTypeSubFilter.ONLY_RESELLER,
            translate: true
          }
        ]
      }
    ];
  }

  private getLabelsCredits(data?: any): string {
    if (data) {
      return data.creditEatMode === CreditEatMode.SUPERACCOUNT_CREDITS
        ? '<b>' + this.getLabelsTranslate('common_shared') + '</b>'
        : this.getLabelsSmsAvailability(data);
    }
  }

  private getLabelsSmsAvailability(data?: any): string {
    let result = '';
    if (data) {
      const ppm = data.smsAvailability?.smsavailability;
      const credits = data.credits;
      result = result + '<b>' + credits + ' ' + getCurrencySymbol() + '</b><br>';
      if (ppm) {
        const newData = [];
        for (const iterator of ppm) {
          const smsAvailability = {
            messageType: iterator.message_type.id_message_type,
            availability: iterator.availability,
            idNation: iterator.nation?.id_nation
          } as SmsAvailability;
          newData.push(smsAvailability);
        }
        const sorted = this.superaccountService.sortPricePerNation(newData, 'messageType', 'idNation', this.user.nation);
        let findNation = false;
        sorted.forEach(p => {
          const mt = p.messageType.toLowerCase();
          const up = p.availability;
          const idNation = p.idNation;
          if (!(idNation && mt === 'gp' && idNation !== this.user.nation)) {
            result = result + this.getSmsAvailabilityTranslate(mt, up, idNation) + '<br>';
          } else {
            findNation = true;
          }
        });
        if (findNation) {
          result += this.getLabelsTranslate('foreign_countries_rate_plan_found');
        }
      }
    }
    return result;
  }

  private getSmsAvailabilityTranslate(label: string, item?: string, idNation?: string): string {
    const valueLabel =
      idNation && label === 'gp' ? this.findLabelNation(idNation) : label ? this.getLabelsTranslate(PrefixLabels.CREDIT_TYPE + label) : '';
    return valueLabel + ': ' + item;
  }

  private getLabelsTranslate(label: string) {
    let labels;
    this.translateService.get(label).subscribe(l => {
      labels = l;
    });
    return labels;
  }

  findLabelNation(id) {
    const nation = _.find(this.nations, { id: id });
    if (nation) {
      if (id === this.user.nation) {
        return this.translateService.instant('credit_type_gp');
      }
      return nation.label;
    }
    return id + ' - unknow';
  }
}
